import { isDispatchRetryBlocked, isDispatchConfirmationPending, isDispatchAcceptedPending } from '../../utils/outboundDispatch'
import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useParams } from "react-router-dom"
import styles from "./message.module.css"
import moment from "moment"
import { adjustTextByIndentation, getLocalDate, getNameUserById, openLinkExternal } from "../../utils/utils"
import { MdAccessTime, MdAdd, MdArrowBack, MdChatBubbleOutline, MdHistory, MdKeyboardArrowDown, MdKeyboardDoubleArrowLeft, MdOutlineAddReaction, MdOutlineClose, MdOutlineContactPage, MdOutlineDelete, MdOutlineFormatListBulleted, MdOutlineGifBox, MdOutlineImage, MdOutlineInfo, MdOutlineInsertDriveFile, MdOutlineKeyboardDoubleArrowRight, MdOutlineLocationOn, MdOutlineMicNone, MdOutlineRepeat, MdOutlineShield, MdOutlineStarBorder, MdOutlineVideocam } from "react-icons/md"
import AudioPlayer from "../Form/AudioPlayer"
import Sticker from "./Elements/Sticker"
import Poll from "./Elements/Poll"
import Template from "./Elements/Template"
import List from "./Elements/List"
import Location from "./Elements/Location"
import Gif from "./Elements/Gif"
import Contact from "./Elements/Contact"
import Document from "./Elements/Document"
import Video from "./Elements/Video"
import Image from "./Elements/Image"
import MediaPending from "./Elements/MediaPending"
import { formatSecondsToTime, formatTextByElipsis } from "../../utils/formatter"
import { HiOutlinePencil } from "react-icons/hi"
import { toast } from "react-toastify"
import ModalConfirm from "../Modal/ModalConfirm"
import ModalEdit from "../Modal/Chat/ModalEdit"
import ModalInfo from "../Modal/ModalInfo"
import ImageWithFallback from "../ImageWithFallback"
import { useData } from "../../contexts/dataContext"
import CircularProgress from "../CircularProgress"
import FormGroupExpand from "../Form/FormGroupExpand"
import Download from "./Elements/Download"
import SystemMessage from "./SystemMessage"
import DivisionMessage from "./DivisionMessage"
import Emoji from "./Emoji"
import ReactionsList from "./ReactionsList"
import Button from "../Form/Button"
import MessageFooter from "./Elements/MessageFooter"
import MessageReactions from "./Elements/MessageReactions"
import ProfileNameByMessage from "./ProfileNameByMessage"
import ProfileImageByMessage from "./ProfileImageByMessage"
import { favoriteSticker } from "../../schemas/ChatMedia"
import { useAuth } from "../../contexts/authContext"
import { useMessageActions } from "./MessageActionsPopover"

const FORWARDABLE_MESSAGE_TYPES = ["text", "image", "video", "gif", "document", "audio", "sticker", "location", "contact", "list"]
const Emojis = lazy(() => import("./Emojis"))

const getUploadTypeLabel = (type = "") => {
    if (type === "image") return "Imagem"
    if (type === "video") return "Video"
    if (type === "audio") return "Audio"
    if (type === "document") return "Arquivo"
    return "Arquivo"
}

const getNewDayDivision = (created_at, lastMessageDate = new Date()) => {
    const currentDate = getLocalDate(created_at).startOf("day")
    const lastDate = getLocalDate(lastMessageDate).startOf("day")
    const diff = currentDate.diff(lastDate, "days")
    const diffToday = Math.abs(getLocalDate().startOf("day").diff(currentDate, "days"))
    return diff !== 0 ? (diff > 0 ? currentDate.format("DD/MM/YYYY") : (diffToday > 1 ? currentDate.format("DD/MM/YYYY") : (diffToday > 0 ? "Ontem" : "Hoje"))) : false
}

const getDeliveryState = (message = {}) => {
    if (message.internal) return "internal"
    if (message.direction !== "send") return "received"
    if (isDispatchConfirmationPending(message)) return "confirmation_pending"
    if (isDispatchAcceptedPending(message)) return "accepted"
    if (message.failed) return "failed"
    if (message.read) return "read"
    if (message.delivered) return "delivered"
    if (message.sent) return "sent"
    return "sending"
}

const Message = ({ message, platform, lastMessage, defaultPicture = "https://oktto-model.s3.us-east-1.amazonaws.com/files/default_profile_picture.png", messageScrolled, searchSelected = false, searchQuery = "", showActions = true, showDivisions = true, isGroupConversation = false, forwardSelectionMode = false, forwardSelected = false, readTrackingId, onAudioPlayed = () => { }, getIconMessageByDirectionDeliveredAndRead = () => { }, deleteMessage = () => { }, updateMessage = () => { }, replyMessage = () => { }, resendMessage = () => { }, downloadMedia = () => { }, reactByMessage = () => { }, scrollToMessage = () => { }, startForwardMessage = () => { }, toggleForwardMessage = () => { }, replyPrivatelyByMessage = () => { }, openPrivateConversationByMessage = () => { }, editContactByMessage = () => { }, openMediaGallery = null }) => {
    const [showPreview, setShowPreview] = useState(false)
    const [showOptions, setShowOptions] = useState(false)
    const [optionsMenuMode, setOptionsMenuMode] = useState("all")
    const [showModalDelete, setShowModalDelete] = useState(false)
    const [showModalEdit, setShowModalEdit] = useState(false)
    const [showModalData, setShowModalData] = useState(false)
    const [dataMessage, setDataMessage] = useState()
    const [showModalReactions, setShowModalReactions] = useState(false)
    const [showModalVersions, setShowModalVersions] = useState(false)
    const [showContactActions, setShowContactActions] = useState(false)
    const [active, setActive] = useState(false)
    const { allUsersGroup } = useData()
    const { user } = useAuth()
    const { openMenu: openActionsMenu, closeMenu: closeActionsMenu } = useMessageActions({ optional: !showActions })
    const userId = user?.id || user?._id
    const historical = message?._archive_tier === "cold" || message?._archive_tier === "s3"

    const { account_id } = useParams()

    const refAction = useRef()
    const reactionActionRef = useRef()
    const contactActionsRef = useRef()
    const contactActionsAvailable = isGroupConversation && !message?.internal && message?.direction !== "send" && !!message?.contact_id && !message?.deleted?.forEveryone
    const processing = message?.content?.processing
    const showTransferStatus = Boolean(message?.uploading || (processing && !message?.sent && !message?.failed))

    const newDayDivision = useMemo(() => {
        return getNewDayDivision(message.created_at, lastMessage?.created_at)
    }, [message?.created_at, lastMessage?.created_at])

    const getTransferStatusData = () => {
        if (message?.uploading) {
            const percent = Math.min(100, Math.max(0, Math.round(Number(message?.percentUpload || 0))))
            return {
                percent,
                label: percent >= 100 ? "Preparando envio" : `Enviando ${getUploadTypeLabel(message?.type).toLowerCase()}`,
                detail: percent >= 100 ? "Upload concluido" : `${percent}% enviado`,
                stage: "uploading"
            }
        }

        const percent = Math.min(100, Math.max(0, Math.round(Number(processing?.progress || 0))))
        const stageDetails = {
            queued: "Aguardando vez",
            preparing: `${percent}% de progresso`,
            downloading: `${percent}% de progresso`,
            analyzing: "Lendo informacoes do video",
            remuxing: `${percent}% de progresso`,
            encoding: `${percent}% de progresso`,
            compressing: `${percent}% de progresso`,
            uploading: "Salvando arquivo final",
            sending: "Processamento concluido"
        }

        return {
            percent,
            label: processing?.label || "Processando arquivo",
            detail: stageDetails[processing?.stage] || (percent > 0 ? `${percent}% de progresso` : "Aguardando inicio"),
            stage: processing?.stage || "processing"
        }
    }

    const getTransferIcon = () => {
        if (message?.type === "image") return <MdOutlineImage />
        if (message?.type === "video") return <MdOutlineVideocam />
        if (message?.type === "audio") return <MdOutlineMicNone />
        return <MdOutlineInsertDriveFile />
    }

    const renderTransferStatus = () => {
        if (!showTransferStatus) return null

        const status = getTransferStatusData()

        return (
            <div className={`${styles.uploading} ${styles[status.stage] || ""}`}>
                <div className={styles.uploadingIcon}>
                    {getTransferIcon()}
                </div>
                <div className={styles.uploadingInfo}>
                    <strong>{status.label}</strong>
                    <span>{status.detail}</span>
                    <div className={styles.uploadingBar}>
                        <div style={{ width: `${status.percent}%` }} />
                    </div>
                </div>
                <CircularProgress value={status.percent} size={42} strokeWidth={3} />
            </div>
        )
    }

    const showDataBySender = useMemo(() => {
        return newDayDivision
            || !lastMessage
            || lastMessage?.direction !== message.direction
            || lastMessage?.user_id !== message.user_id
            || lastMessage?.bot_details?.agent_id !== message?.bot_details?.agent_id
            || lastMessage?.contact_id !== message.contact_id
            || lastMessage?.internal !== message.internal
            || lastMessage?.type === "system"
    }, [
        newDayDivision,
        lastMessage?.direction,
        lastMessage?.user_id,
        lastMessage?.bot_details?.agent_id,
        lastMessage?.contact_id,
        lastMessage?.internal,
        lastMessage?.type,
        message.direction,
        message.user_id,
        message?.bot_details?.agent_id,
        message.contact_id,
        message.internal,
    ])

    useEffect(() => {
        if (!showContactActions) {
            return
        }

        const closeContactActions = () => setShowContactActions(false)
        window.addEventListener("click", closeContactActions)

        return () => window.removeEventListener("click", closeContactActions)
    }, [showContactActions])

    useEffect(() => {
        const normalizedScrolledId = messageScrolled?.toString?.()
        const isSelectedMessage = normalizedScrolledId && [message?.message_id, message?._id, message?.temp_id]
            .some(messageId => messageId?.toString?.() === normalizedScrolledId)
        setActive(Boolean(isSelectedMessage))
    }, [messageScrolled, message?.message_id, message?._id, message?.temp_id])

    const getMessageByTypeAndContent = (type, content, origin) => {
        return (
            <div className={`${styles.content} ${message?.reactions?.length > 0 ? styles.hasReaction : ''}`}>
                {!message.internal &&
                    <div
                        className={`${styles.profileImage} ${contactActionsAvailable && showDataBySender ? styles.clickableProfileImage : ""}`}
                        onClick={showDataBySender ? handleToggleContactActions : undefined}
                        title={contactActionsAvailable && showDataBySender ? "Ações do contato" : undefined}
                    >
                        {showDataBySender && <ProfileImageByMessage message={message} platform={platform} />}
                    </div>
                }
                <div className={`${styles.groupContentMessage} ${showDataBySender ? styles.withTail : styles.withoutTail}`}>
                    {message.deleted?.status && <div className={styles.italicText}><MdOutlineDelete /> <span>Mensagem apagada {message.deleted?.forEveryone ? "para todos" : "para você"}  {message.deleted?.user_id ? `por ${getNameUserById(allUsersGroup, message.deleted.user_id)}` : ''}</span></div>}
                    {!!message?.scheduled_date && <div className={styles.italicText}><MdAccessTime /> <span>{message?.metadata?.type === "ai_follow_up" ? `Retomada automática pendente${message?.metadata?.ai_follow_up?.sequence ? `, tentativa ${message.metadata.ai_follow_up.sequence}` : ""}` : "Mensagem com envio agendado"}</span></div>}
                    {showDataBySender &&
                        <div className={styles.senderInfo}>
                            {message.internal &&
                                <span className={styles.internalBadge}>
                                    <MdOutlineShield />
                                    Interna
                                </span>
                            }
                            {contactActionsAvailable ?
                                <>
                                    <button type="button" className={styles.senderContactTrigger} onClick={handleToggleContactActions}>
                                        <span className={styles.profileName}>{<ProfileNameByMessage message={message} platform={platform} />}</span>
                                        <MdKeyboardArrowDown />
                                    </button>
                                    {showContactActions &&
                                        <div className={styles.contactActionsMenu} ref={contactActionsRef} onClick={(e) => e.stopPropagation()}>
                                            <button type="button" onClick={handleOpenPrivateConversationByContactIndicator}>
                                                <MdChatBubbleOutline />
                                                <span>Conversar com contato</span>
                                            </button>
                                            <button type="button" onClick={handleEditContactByMessage}>
                                                <HiOutlinePencil />
                                                <span>Editar contato</span>
                                            </button>
                                        </div>
                                    }
                                </>
                                :
                                <span className={styles.profileName}>{<ProfileNameByMessage message={message} platform={platform} />}</span>
                            }
                        </div>
                    }
                    {forwardSelectionMode &&
                        <button
                            type="button"
                            className={`${styles.forwardSelector} ${forwardSelected ? styles.selected : ""}`}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                toggleForwardMessage(message)
                            }}
                            title={forwardSelected ? "Remover do encaminhamento" : "Selecionar para encaminhar"}
                        >
                            {forwardSelected && <span />}
                        </button>
                    }
                    {showActions && !forwardSelectionMode &&
                        <button
                            type="button"
                            className={`${styles.options} ${showOptions && optionsMenuMode === "all" ? styles.active : ''}`}
                            onClick={handleToogleShowoptions}
                            ref={refAction}
                            title="Opções da mensagem"
                            aria-label="Opções da mensagem"
                        >
                            <MdKeyboardArrowDown />
                        </button>
                    }
                    {content?.quoted_message &&
                        getContentByQuoted(content?.quoted_message)
                    }
                    {origin &&
                        getOrigin(origin)
                    }
                    {message.forwarded?.status && <div className={styles.italicText}><MdOutlineKeyboardDoubleArrowRight /> <span>Encaminhada  {message.forwarded?.user_id ? `por ${getNameUserById(allUsersGroup, message.forwarded.user_id)}` : ''}</span></div>}
                    <div className={styles.contentMessage} style={{ marginTop: showDataBySender || type === "text" ? '0' : '1em' }}>
                        <div className={styles.groupMedia}>
                            {renderedMessageContent}
                            {renderTransferStatus()}
                        </div>
                        <MessageFooter
                            created_at={message.created_at}
                            edited={message.edited}
                            direction={message.direction}
                            sent={message.sent}
                            delivered={message.delivered}
                            read={message.read}
                            failed={message.failed}
                            internal={message.internal}
                            error={message.error}
                            confirmationPending={isDispatchConfirmationPending(message)}
                            acceptancePending={isDispatchAcceptedPending(message)}
                            styles={styles}
                            getIcon={getIconMessageByDirectionDeliveredAndRead}
                            allUsersGroup={allUsersGroup}
                        />
                    </div>
                    <MessageReactions
                        reactions={message.reactions}
                        styles={styles}
                        onClick={handleOpenReactionsMessage}
                    />
                </div>
                {showActions && !forwardSelectionMode && !historical && (!message?.deleted?.status || !message?.deleted?.forEveryone) && ReactionsList.length > 0 &&
                    <div className={styles.messageActions}>
                        <button
                            type="button"
                            className={`${styles.reactionAction} ${showOptions && optionsMenuMode === "reactions" ? styles.active : ''}`}
                            onClick={handleToggleReactionOptions}
                            ref={reactionActionRef}
                            title="Reagir à mensagem"
                            aria-label="Reagir à mensagem"
                        >
                            <MdOutlineAddReaction />
                        </button>
                    </div>
                }
            </div>
        )
    }

    const getContentByQuoted = (message) => {
        return (
            <div className={`${styles.contentMessage} ${styles.quotedMessage}`} onClick={() => scrollToMessage(message?.message_id)}>
                <span className={styles.senderInfo}>{<ProfileNameByMessage message={message} platform={platform} />}</span>
                <div className={styles.groupMedia}>
                    {getContentMessageByType(message?.type, message?.content, false, true)}
                </div>
            </div>
        )
    }

    const getDownloadFallback = (props = {}) => historical
        ? <div className={styles.defaultMessage}><span>Arquivo histórico não persistido</span></div>
        : <Download
            {...props}
            downloading={message?.content?.downloading}
            downloadMedia={() => downloadMedia(message)}
        />

    const getContentMessageByType = (type, content, showPreview = false, quotedMessage = false, uploading = false) => {
        const text = adjustTextByIndentation(content?.text || "", account_id, {
            renderWhatsAppEmojis: true,
            highlightQuery: searchSelected ? searchQuery : "",
        })
        if (type === "media_pending" || (type === "text" && content?.media_status === "pending")) {
            return <MediaPending compact={quotedMessage} />
        } else if (type === "text") {
            return <>
                <span className={styles.text}>{text}</span>
            </>
        } else if (type === "image") {
            return <>
                {quotedMessage && <div className={styles.defaultMessage}><MdOutlineImage /><span>{content?.text ? (content?.text?.length > 100 ? `${content.text.substring(0, 100)}...` : content?.text) : "Imagem"}</span></div>}
                {content?.url || quotedMessage ?
                    <Image
                        url={showPreview ? content?.urlThumb || content?.url : content?.url || content?.urlThumb}
                        width={content?.width}
                        height={content?.height}
                        showPreview={showPreview}
                        openPreview={openPreview}
                        quotedMessage={quotedMessage}
                    />
                    :
                    getDownloadFallback()
                }
                {showPreview && content?.text && <span>{text}</span>}
            </>
        } else if (type === "video") {
            return <>
                {quotedMessage && <div className={styles.defaultMessage}><MdOutlineVideocam /><span>{content?.text ? (content?.text?.length > 100 ? `${content.text.substring(0, 100)}...` : content?.text) : "Vídeo"}</span></div>}
                {content?.url || quotedMessage ?
                    <Video
                        url={content?.url}
                        showPreview={showPreview}
                        openPreview={openPreview}
                        quotedMessage={quotedMessage}
                        showPlayAction={!uploading}
                    />
                    :
                    getDownloadFallback()
                }
                {showPreview && content?.text && <span>{text}</span>}
            </>
        } else if (type === "audio") {
            return quotedMessage ?
                <>
                    <div className={`${styles.defaultMessage} ${styles.text}`}>
                        <MdOutlineMicNone /><span>{formatSecondsToTime(content?.seconds, false)}</span>
                    </div>
                </>
                :
                <>
                    {content?.url ?
                        <div className={styles.audioMessage}>
                            <AudioPlayer
                                src={content?.url}
                                pink={!message.internal}
                                peaks={content?.waveform}
                                contrast={message.direction === "send" && !message.internal}
                                onPlay={() => onAudioPlayed(message)}
                            />
                        </div>
                        :
                        getDownloadFallback({ isAudio: true })
                    }
                    {message?.transcribed &&
                        <FormGroupExpand labelText={<span className={styles.title}>Transcrição</span>} startShow={false}>
                            <div className={styles.description}>
                                {content?.text}
                            </div>
                        </FormGroupExpand>
                    }
                </>
        } else if (type === "document") {
            return quotedMessage ?
                <>
                    <div className={`${styles.defaultMessage} ${styles.text}`}>
                        <MdOutlineInsertDriveFile /><span>{content?.text ? text : content.name}</span>
                    </div>
                </>
                :
                <>
                    {content?.url ?
                        <Document name={content?.name} url={content?.url} size={content?.size} mimetype={content?.mimetype} />
                        :
                        getDownloadFallback()
                    }
                    {content?.text && <span>{text}</span>}
                </>
        } else if (type === "contact") {
            return quotedMessage ?
                <>
                    <div className={`${styles.defaultMessage} ${styles.text}`}>
                        <MdOutlineContactPage /><span>{getNameByContacts(content?.contacts || [])}</span>
                    </div>
                </>
                : <>
                    <Contact contacts={content?.contacts || []} defaultPicture={defaultPicture} />
                </>
        } else if (type === "location") {
            return <>
                {quotedMessage && <div className={styles.defaultMessage}><MdOutlineLocationOn /><span>{content?.text ? (content?.text?.length > 100 ? `${content.text.substring(0, 100)}...` : content?.text) : "Localização"}</span></div>}
                <Location
                    latitude={content?.latitude}
                    longitude={content?.longitude}
                    quotedMessage={quotedMessage}
                />
                {!quotedMessage && content?.address && <span className={styles.address}>{content?.address}</span>}
            </>
        } else if (type === "gif") {
            return <>
                {quotedMessage && <div className={styles.defaultMessage}><MdOutlineGifBox /><span>{content?.text ? (content?.text?.length > 100 ? `${content.text.substring(0, 100)}...` : content?.text) : "GIF"}</span></div>}
                {content?.url || quotedMessage ?
                    <Gif
                        url={content?.url}
                        width={content?.width}
                        height={content?.height}
                        attribution={content?.attribution || (content?.provider === "giphy" ? "GIPHY" : content?.gif_attribution)}
                        quotedMessage={quotedMessage}
                    />
                    :
                    getDownloadFallback()
                }
                {!quotedMessage && content?.text && <span>{text}</span>}
            </>
        } else if (type === "sticker") {
            return <>
                {content?.url || quotedMessage ?
                    <Sticker url={content?.url} animated={content?.animated || content?.is_lottie} />
                    :
                    getDownloadFallback({ width: 150, height: 150 })
                }
                {content?.text && <span>{text}</span>}
            </>
        } else if (type === "poll") {
            return quotedMessage ?
                <>
                    <div className={`${styles.defaultMessage} ${styles.text}`}>
                        <MdOutlineFormatListBulleted /><span>{content?.name || "Enquete"}</span>
                    </div>
                </>
                : <>
                    <Poll content={content} />
                    {content?.text && <span>{text}</span>}
                </>
        } else if (type === "list") {
            return quotedMessage ?
                <>
                    <div className={`${styles.defaultMessage} ${styles.text}`}>
                        <MdOutlineFormatListBulleted /><span>{formatTextByElipsis(content?.title, 30) || formatTextByElipsis(content?.description, 30) || "Lista"}</span>
                    </div>
                </>
                : <>
                    <List content={content} />
                    {content?.text && <span>{text}</span>}
                </>
        } else if (type === "listResponse") {
            return quotedMessage ?
                <>
                    <div className={`${styles.defaultMessage} ${styles.text}`}>
                        <span>{content?.text}</span>
                    </div>
                </>
                : <>
                    {content?.text && <span>{text}</span>}
                </>
        } else if (type === "template") {
            return quotedMessage ?
                <>
                    <div className={`${styles.defaultMessage} ${styles.text}`}>
                        <span>{content?.title || "Template"}</span>
                    </div>
                </>
                : <>
                    <Template
                        content={content}
                        showPreview={showPreview}
                        openPreview={openPreview}
                        downloadMedia={() => downloadMedia(message)}
                        highlightQuery={searchSelected ? searchQuery : ""}
                    />
                </>
        } else if (type === "unsupported") {
            return quotedMessage ?
                <>
                    <div className={styles.defaultMessage}>
                        <MdOutlineInfo />
                        <span>Mensagem não suportada</span>
                    </div>
                </>
                :
                <div className={styles.unsupportedMessage}>
                    <div className={styles.unsupportedIcon}>
                        <MdOutlineInfo />
                    </div>
                    <div className={styles.unsupportedText}>
                        <strong>Mensagem não suportada</strong>
                        <span>{content?.text || "Este tipo de mensagem não pode ser exibido na caixa de entrada."}</span>
                        {content?.hint && <small>{content.hint}</small>}
                    </div>
                </div>
        } else if (text) {
            return <>
                <span className={styles.text}>{text}</span>
            </>
        } else {
            return <>
                <span>Mensagem não suportada</span>
            </>
        }
    }

    const getOrigin = (origin) => {
        if (!origin || origin.type === "organic") {
            return null
        }

        const meta = origin.metadata || {}
        const channel = origin.channel ? origin.channel.charAt(0).toUpperCase() + origin.channel.slice(1) : null
        const title = meta.ad_title
        const description = meta.ad_body || origin.description || ""
        const formattedDescription = description ? adjustTextByIndentation(description, account_id) : ""
        const image = Boolean(meta.ad_thumbnail_url || meta.ad_original_image)

        const cta =
            meta.ad_source_type === "website" && meta.ad_source_url
                ? { label: "Acessar site", url: meta.ad_source_url }
                : meta.ad_source_url
                    ? { label: "Ver anúncio", url: meta.ad_source_url }
                    : null;

        if (!title && !formattedDescription && !image && !cta) {
            return null
        }

        return (
            <div className={styles.originCard}>
                {channel &&
                    <div className={styles.originHeader}>
                        <span className={styles.originTitle}>Origem: {channel}</span>
                    </div>
                }

                <div className={styles.originContent}>
                    {image && (
                        <div className={styles.originImageContainer}>
                            <ImageWithFallback
                                src={meta.ad_thumbnail_url}
                                alt="Imagem do anúncio"
                                className={styles.originImage}
                                functionError={() => <img src={meta.ad_original_image} className={styles.originImage} alt="Imagem do anúncio" />}
                            />
                        </div>
                    )}
                    <div className={styles.originTextContent}>
                        <div className={styles.originAdTitle}>
                            {title || "Anúncio patrocinado"}
                        </div>

                        {formattedDescription && (
                            <div className={styles.originAdDescription}>
                                {formattedDescription}
                            </div>
                        )}

                        {cta &&
                            <Button variant="secondary"
                                onClick={() => openLinkExternal(cta.url)}
                            >{cta.label}</Button>
                        }
                    </div>
                </div>
            </div>
        )
    }

    const getNameByContacts = (contacts) => {
        const names = contacts?.map(contact => contact.displayName).join(", ")
        return names
    }

    const openPreview = (showPreview) => {
        if (showPreview) {
            if (!message?.internal && typeof openMediaGallery === "function" && ["image", "video"].includes(message?.type)) {
                openMediaGallery(message)
                return
            }
            setShowPreview(true)
        }
    }

    const closePreview = () => {
        setShowPreview(false)
    }

    useEffect(() => {
        if (!showPreview) {
            return
        }

        const handlePreviewKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault()
                closePreview()
            }
        }

        window.addEventListener("keydown", handlePreviewKeyDown)
        return () => window.removeEventListener("keydown", handlePreviewKeyDown)
    }, [showPreview])

    const renderedMessageContent = useMemo(() => {
        return getContentMessageByType(message.type, message.content, true, false, message?.uploading)
    }, [message.type, message.content, message?.uploading, account_id, searchQuery, searchSelected])

    const toggleOptionsMenu = (e, mode = "all", trigger = refAction.current) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (showActions) {
            const shouldClose = showOptions && optionsMenuMode === mode
            if (shouldClose) {
                closeActionsMenu({ restoreFocus: true })
                return
            }
            setOptionsMenuMode(mode)
            openActionsMenu({
                anchor: trigger,
                event: e,
                mode,
                direction: message?.direction,
                content: getOptionsByMessage(mode === "reactions"),
                menuClassName: styles.optionsMenu,
                compactClassName: styles.reactionsOnlyMenu,
                backgroundClassName: styles.optionsBackground,
                ariaLabel: mode === "reactions" ? "Reagir à mensagem" : "Ações da mensagem",
                estimatedHeight: mode === "reactions" ? 44 : getOptionsMenuHeight(),
                onClose: () => setShowOptions(false),
            })
            setShowOptions(true)
        }
    }

    const handleToogleShowoptions = (e) => toggleOptionsMenu(e, "all", refAction.current)
    const handleToggleReactionOptions = (e) => toggleOptionsMenu(e, "reactions", reactionActionRef.current)

    const getOptionsMenuHeight = () => {
        const directionIsSend = message?.direction === "send"
        const withError = message?.failed
        const scheduled = !!message?.scheduled_date
        const messageNotDeletedForEveryone = (!message?.deleted?.status || !message?.deleted?.forEveryone)
        const diffMinutes = moment().diff(moment(message.created_at), "minutes")
        const rows = [
            !historical && directionIsSend && messageNotDeletedForEveryone && diffMinutes < 1440,
            !historical && !scheduled && !withError && directionIsSend && messageNotDeletedForEveryone && diffMinutes < 150,
            !scheduled && !withError && !message?.internal && messageNotDeletedForEveryone && FORWARDABLE_MESSAGE_TYPES.includes(message?.type),
            !scheduled && !withError && messageNotDeletedForEveryone,
            isGroupConversation && !message?.internal && !directionIsSend && !!message?.contact_id && messageNotDeletedForEveryone,
            isGroupConversation && !message?.internal && !directionIsSend && !!message?.contact_id && messageNotDeletedForEveryone,
            !scheduled && !withError && messageNotDeletedForEveryone && message?.type === "sticker",
            !historical && withError && !isDispatchRetryBlocked(message),
            !scheduled && directionIsSend,
            message?.edited?.status && Array.isArray(message?.versions) && message.versions.length > 0,
            !historical && messageNotDeletedForEveryone,
        ].filter(Boolean).length

        return Math.max(44, rows * 38)
    }

    const getOptionsByMessage = (reactionsOnly = false) => {
        const diffMinutes = moment().diff(moment(message.created_at), "minutes")
        const directionIsSend = message?.direction === "send"
        const withError = message?.failed
        const scheduled = !!message?.scheduled_date
        const messageNotDeletedForEveryone = (!message?.deleted?.status || !message?.deleted?.forEveryone)
        const platformSupportsMessageEdit = message?.internal || platform === "whatsapp"
        const quickReactions = ReactionsList
        const deleteAvailable = !historical && directionIsSend && messageNotDeletedForEveryone && diffMinutes < 1440
        const editAvailable = !historical && platformSupportsMessageEdit && !scheduled && !withError && directionIsSend && messageNotDeletedForEveryone && diffMinutes < 150
        const forwardAvailable = !scheduled && !withError && !message?.internal && messageNotDeletedForEveryone && FORWARDABLE_MESSAGE_TYPES.includes(message?.type)
        const replyAvailable = !scheduled && !withError && messageNotDeletedForEveryone
        const dataAvailable = !scheduled && directionIsSend
        const reactionAvailable = !historical && messageNotDeletedForEveryone && quickReactions.length > 0
        const versionsAvailable = message?.edited?.status && Array.isArray(message?.versions) && message.versions.length > 0
        const favoriteStickerAvailable = !scheduled && !withError && messageNotDeletedForEveryone && message?.type === "sticker"
        const privateConversationAvailable = isGroupConversation && !message?.internal && !directionIsSend && !!message?.contact_id && messageNotDeletedForEveryone
        const reactionOptions = reactionAvailable && <li className={styles.listReactions}>
            <div className={styles.quickReactions}>
                {quickReactions.map((emoji, key) => (
                    <Emoji
                        key={key}
                        icon={emoji.icon}
                        onClick={handleReactByMessage}
                    />
                ))}
                <button
                    type="button"
                    className={styles.openAllReactions}
                    title="Mais emojis"
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleOpenAllReactions(e)
                    }}
                >
                    <MdAdd />
                </button>
            </div>
        </li>

        if (reactionsOnly) return reactionOptions

        return (
            <>
                {deleteAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleOpenModalDelete}><MdOutlineDelete /> Apagar</button></li>}
                {editAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleOpenModalEdit}><HiOutlinePencil /> Editar</button></li>}
                {forwardAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleStartForwardMessage}><MdOutlineKeyboardDoubleArrowRight /> Encaminhar</button></li>}
                {replyAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleReplyMessage}><MdKeyboardDoubleArrowLeft /> Responder</button></li>}
                {privateConversationAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleReplyPrivatelyByMessage}><MdKeyboardDoubleArrowLeft /> Responder no privado</button></li>}
                {privateConversationAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleOpenPrivateConversationByMessage}><MdChatBubbleOutline /> Conversar com contato</button></li>}
                {favoriteStickerAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleFavoriteSticker}><MdOutlineStarBorder /> Favoritar figurinha</button></li>}
                {!historical && withError && !isDispatchRetryBlocked(message) && <li role="none"><button role="menuitem" type="button" onClick={handleResendMessage}><MdOutlineRepeat /> Enviar novamente</button></li>}
                {dataAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleOpenDataMessage}><MdOutlineInfo /> Dados</button></li>}
                {versionsAvailable && <li role="none"><button role="menuitem" type="button" onClick={handleOpenVersionsMessage}><MdHistory /> Ver edições</button></li>}
                {reactionOptions}
            </>
        )
    }

    const handleOpenAllReactions = (event) => {
        setOptionsMenuMode("emojis")
        openActionsMenu({
            anchor: reactionActionRef.current || event?.currentTarget,
            event,
            mode: "emojis",
            direction: message?.direction,
            content: <li className={styles.fullReactionPicker} onClick={(e) => e.stopPropagation()}>
                <Suspense fallback={null}>
                    <Emojis onClick={handleReactByMessage} />
                </Suspense>
            </li>,
            menuClassName: styles.optionsMenu,
            compactClassName: styles.reactionsOnlyMenu,
            backgroundClassName: styles.optionsBackground,
            ariaLabel: "Escolher reação",
            estimatedHeight: 450,
            onClose: () => setShowOptions(false),
        })
        setShowOptions(true)
    }

    const handleReactByMessage = (text) => {
        reactByMessage({ message_id: message._id, text, conversation_id: message.conversation_id, inbox_id: message.inbox_id })
        closeActionsMenu()
    }

    const isOwnReaction = (reaction = {}) => {
        if (reaction.direction !== "send") return false
        return !reaction.user_id || reaction.user_id?.toString?.() === userId?.toString?.()
    }

    const handleRemoveReactionByMessage = (reaction) => {
        if (!isOwnReaction(reaction)) return

        reactByMessage({
            message_id: message._id,
            text: "",
            remove_reaction: true,
            conversation_id: message.conversation_id,
            inbox_id: message.inbox_id
        })
    }

    const handleOpenModalDelete = () => {
        setShowModalDelete(true)
    }

    const handleCloseModalDelete = () => {
        setShowModalDelete(false)
    }

    const handleDeleteMessage = () => {
        deleteMessage({ message_id: message._id, conversation_id: message.conversation_id, inbox_id: message.inbox_id })
        handleCloseModalDelete()
    }

    const handleOpenModalEdit = () => {
        if (message?.type !== "text") {
            return toast.error("Só é possível editar mensagens de texto!")
        }
        setShowModalEdit(true)
    }

    const handleCloseModalEdit = () => {
        setShowModalEdit(false)
    }

    const handleUpdateMessage = (text) => {
        updateMessage({ message_id: message._id, conversation_id: message.conversation_id, inbox_id: message.inbox_id, text })
    }

    const handleReplyMessage = () => {
        const formattedContent = getContentByQuoted(message)
        replyMessage(message, formattedContent)
    }

    const handleStartForwardMessage = () => {
        startForwardMessage(message)
        setShowOptions(false)
    }

    const handleReplyPrivatelyByMessage = () => {
        replyPrivatelyByMessage(message)
        setShowOptions(false)
    }

    const handleOpenPrivateConversationByMessage = () => {
        openPrivateConversationByMessage(message)
        setShowOptions(false)
    }

    const handleToggleContactActions = (e) => {
        if (!contactActionsAvailable) {
            return
        }

        e?.preventDefault()
        e?.stopPropagation()
        setShowContactActions(previousValue => !previousValue)
    }

    const handleOpenPrivateConversationByContactIndicator = (e) => {
        e?.preventDefault()
        e?.stopPropagation()
        openPrivateConversationByMessage(message)
        setShowContactActions(false)
    }

    const handleEditContactByMessage = (e) => {
        e?.preventDefault()
        e?.stopPropagation()
        editContactByMessage(message)
        setShowContactActions(false)
    }

    const handleFavoriteSticker = async () => {
        try {
            await favoriteSticker(message?.content)
            toast.success("Figurinha adicionada aos favoritos.")
            setShowOptions(false)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível favoritar a figurinha.")
        }
    }

    const handleResendMessage = () => {
        if (isDispatchRetryBlocked(message)) return
        resendMessage(message)
    }

    const handleOpenDataMessage = () => {
        setShowModalData(true)
        setDataMessage(
            <div>
                <p><b>Enviada:</b> {message?.metadata?.sent_at ? moment(message.metadata.sent_at).format("DD/MM/YYYY HH:mm:ss") : "-"}</p>
                <p><b>Entregue:</b> {message?.metadata?.delivered_at ? moment(message.metadata.delivered_at).format("DD/MM/YYYY HH:mm:ss") : "-"}</p>
                <p><b>Lida:</b> {message?.metadata?.read_at ? moment(message.metadata.read_at).format("DD/MM/YYYY HH:mm:ss") : "-"}</p>
            </div>
        )
    }

    const handleCloseDataMessage = () => {
        setShowModalData(false)
        setDataMessage()
    }

    const handleOpenReactionsMessage = () => {
        setShowModalReactions(true)
    }

    const handleCloseReactionsMessage = () => {
        setShowModalReactions(false)
    }

    const handleOpenVersionsMessage = () => {
        setShowModalVersions(true)
    }

    const handleCloseVersionsMessage = () => {
        setShowModalVersions(false)
    }

    const getVersionText = (content = {}) => {
        const text = content?.text || content?.caption || content?.description || content?.title || content?.name
        if (text) return text
        if (content?.contacts?.length) return getNameByContacts(content.contacts)
        if (content?.latitude && content?.longitude) return `${content.latitude}, ${content.longitude}`
        if (content?.url || content?.keyFileS3 || content?.attachment_id) return "Mídia/anexo"
        return "Conteúdo sem texto"
    }

    const getMessageVersions = () => {
        const previousVersions = Array.isArray(message?.versions) ? message.versions : []
        const currentVersion = {
            content: message?.content || {},
            created_at: message?.updated_at || message?.edited?.updated_at || message?.created_at,
            current: true,
        }

        return [...previousVersions, currentVersion]
            .filter(version => version?.content)
            .map((version, index, list) => ({
                ...version,
                label: version.current ? "Atual" : `Versão ${index + 1}`,
                position: list.length - index,
            }))
            .reverse()
    }

    return (
        <>
            {showDivisions && newDayDivision &&
                <DivisionMessage
                    text={newDayDivision}
                />
            }
            {message.type === "system" ?
                <SystemMessage
                    message={message}
                    allUsersGroup={allUsersGroup}
                    platform={platform}
                />
                :
                <div
                    className={`${styles.groupMessage} ${message.direction === "send" ? styles.send : ''} ${message.internal ? styles.internal : ''} ${message.deleted?.status ? styles.deleted : ''} ${active ? styles.active : ''} ${searchSelected ? styles.searchSelected : ''} ${showContactActions ? styles.contactActionsOpen : ''} ${forwardSelectionMode ? styles.forwardMode : ''} ${forwardSelected ? styles.forwardSelected : ''}`}
                    data-message-id={message?.message_id}
                    data-message-record-id={message?._id}
                    data-message-temp-id={message?.temp_id}
                    data-read-message-id={readTrackingId}
                    data-message-type={message?.type}
                    data-delivery-state={getDeliveryState(message)}
                >
                    <div className={styles.message} onContextMenu={handleToogleShowoptions} onClick={forwardSelectionMode ? () => toggleForwardMessage(message) : undefined}>
                        {getMessageByTypeAndContent(message.type, message.content, message.origin)}
                    </div>
                </div>
            }
            {showPreview && createPortal(
                <div className={styles.preview}>
                    <div className={styles.close} onClick={closePreview}>
                        <MdArrowBack />
                    </div>
                    {getContentMessageByType(message.type, message.content)}
                </div>,
                document.body
            )}
            {showModalDelete &&
                <ModalConfirm
                    closeModal={handleCloseModalDelete}
                    onSubmit={handleDeleteMessage}
                    icon={<MdOutlineDelete />}
                    title="Deseja apagar a mensagem?"
                    description="A mensagem será apagada para todos na conversa!"
                    text="Se deseja manter a mensagem clique em cancelar"
                />
            }
            {showModalEdit &&
                <ModalEdit
                    closeModal={handleCloseModalEdit}
                    onSubmit={handleUpdateMessage}
                    value={message?.content?.text}
                />
            }
            {showModalData &&
                <ModalInfo
                    closeModal={handleCloseDataMessage}
                    icon={<MdOutlineInfo />}
                    content={dataMessage}
                    description="Informações da mensagem"
                    title="Dados"
                />
            }
            {showModalReactions &&
                <ModalInfo
                    icon={<MdOutlineAddReaction />}
                    content={
                        <div className={styles.messageReactions}>
                            {message?.reactions?.map((reaction, index) => (
                                <div className={styles.itemReaction} key={reaction._id || `${reaction.text}-${index}`}>
                                    <div className={styles.senderInfo}>
                                        <div className={styles.profileImage}>
                                            {<ProfileImageByMessage message={reaction} platform={platform} />}
                                        </div>
                                        <div className={styles.reactionSenderText}>
                                            <span className={styles.profileName}>{<ProfileNameByMessage message={reaction} platform={platform} />} {isOwnReaction(reaction) && "(Você)"}</span>
                                            <small>{reaction.direction === "send" ? "Equipe" : "Contato"}</small>
                                        </div>
                                    </div>
                                    <div className={styles.reactionActions}>
                                        <div className={styles.reaction}>
                                            {reaction.text}
                                        </div>
                                        {isOwnReaction(reaction) &&
                                            <button type="button" onClick={() => handleRemoveReactionByMessage(reaction)} title="Remover reação">
                                                <MdOutlineClose />
                                            </button>
                                        }
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                    title="Reações"
                    description="Veja quem reagiu à mensagem"
                    fitContent={true}
                    closeModal={handleCloseReactionsMessage}
                />
            }
            {showModalVersions &&
                <ModalInfo
                    icon={<MdHistory />}
                    content={
                        <div className={styles.messageVersions}>
                            {getMessageVersions().map((version, index) => (
                                <div className={`${styles.messageVersionItem} ${version.current ? styles.currentVersion : ""}`} key={`${version.created_at || "version"}-${index}`}>
                                    <div className={styles.versionHeader}>
                                        <strong>{version.label}</strong>
                                        <span>{version.created_at ? moment(version.created_at).format("DD/MM/YYYY HH:mm:ss") : "-"}</span>
                                    </div>
                                    <div className={styles.versionContent}>
                                        {adjustTextByIndentation(getVersionText(version.content), account_id, { renderWhatsAppEmojis: true })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                    title="Histórico de edições"
                    description="Veja as versões salvas desta mensagem"
                    fitContent={true}
                    closeModal={handleCloseVersionsMessage}
                />
            }
        </>
    )
}

const getMessageId = (message = {}) => message?.message_id || message?._id || message?.temp_id || ""
const isMessageSelected = (message = {}, selectedId) => {
    const normalizedSelectedId = selectedId?.toString?.()
    return Boolean(normalizedSelectedId && [message?.message_id, message?._id, message?.temp_id]
        .some(messageId => messageId?.toString?.() === normalizedSelectedId))
}

const getLastMessageSignature = (message = {}) => {
    if (!message) return ""
    return [
        getMessageId(message),
        message.direction,
        message.user_id,
        message.contact_id,
        message.internal,
        message.type,
        message.created_at,
        message._archive_tier,
        message?.bot_details?.agent_id,
    ].join("|")
}

const areMessagesEqual = (previousMessage = {}, nextMessage = {}) => {
    if (previousMessage === nextMessage) return true
    if (getMessageId(previousMessage) !== getMessageId(nextMessage)) return false

    return previousMessage.type === nextMessage.type
        && previousMessage.direction === nextMessage.direction
        && previousMessage.internal === nextMessage.internal
        && previousMessage.user_id === nextMessage.user_id
        && previousMessage.bot_details?.agent_id === nextMessage.bot_details?.agent_id
        && previousMessage.bot_details?.agent_name === nextMessage.bot_details?.agent_name
        && previousMessage.contact_id === nextMessage.contact_id
        && previousMessage.created_at === nextMessage.created_at
        && previousMessage._archive_tier === nextMessage._archive_tier
        && previousMessage.updated_at === nextMessage.updated_at
        && previousMessage.sent === nextMessage.sent
        && previousMessage.delivered === nextMessage.delivered
        && previousMessage.read === nextMessage.read
        && previousMessage.uploading === nextMessage.uploading
        && previousMessage.percentUpload === nextMessage.percentUpload
        && previousMessage.failed === nextMessage.failed
        && previousMessage.outbound_dispatch?.state === nextMessage.outbound_dispatch?.state
        && previousMessage.scheduled_date === nextMessage.scheduled_date
        && previousMessage.schedule_send === nextMessage.schedule_send
        && previousMessage.content === nextMessage.content
        && previousMessage.metadata?.sent_at === nextMessage.metadata?.sent_at
        && previousMessage.metadata?.delivered_at === nextMessage.metadata?.delivered_at
        && previousMessage.metadata?.read_at === nextMessage.metadata?.read_at
        && previousMessage.reactions === nextMessage.reactions
        && previousMessage.versions === nextMessage.versions
        && previousMessage.edited?.status === nextMessage.edited?.status
        && previousMessage.edited?.user_id === nextMessage.edited?.user_id
        && previousMessage.deleted?.status === nextMessage.deleted?.status
        && previousMessage.deleted?.forEveryone === nextMessage.deleted?.forEveryone
        && previousMessage.deleted?.user_id === nextMessage.deleted?.user_id
}

const areMessagePropsEqual = (previousProps, nextProps) => {
    const wasScrolled = isMessageSelected(previousProps.message, previousProps.messageScrolled)
    const isScrolled = isMessageSelected(nextProps.message, nextProps.messageScrolled)

    return areMessagesEqual(previousProps.message, nextProps.message)
        && getLastMessageSignature(previousProps.lastMessage) === getLastMessageSignature(nextProps.lastMessage)
        && previousProps.platform === nextProps.platform
        && previousProps.defaultPicture === nextProps.defaultPicture
        && previousProps.showActions === nextProps.showActions
        && previousProps.showDivisions === nextProps.showDivisions
        && previousProps.isGroupConversation === nextProps.isGroupConversation
        && previousProps.forwardSelectionMode === nextProps.forwardSelectionMode
        && previousProps.forwardSelected === nextProps.forwardSelected
        && previousProps.searchSelected === nextProps.searchSelected
        && (!nextProps.searchSelected || previousProps.searchQuery === nextProps.searchQuery)
        && wasScrolled === isScrolled
}

export default React.memo(Message, areMessagePropsEqual)
